import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import TenantSidebar from '../../components/tenant/TenantSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toFixed(2);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function TenantDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/tenant/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const { tenant, subscription: sub, recent_invoices } = data || {};

  return (
    <div className="app-layout">
      <Topbar role="tenant" />
      <div className="dashboard-layout">
        <TenantSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Good morning, {tenant?.first_name}!</div>
          <div className="page-sub">{tenant?.unit_number ? `${tenant.unit_number} · ` : ''}{tenant?.property_name} · {tenant?.address}</div>

          {tenant?.status === 'pending_verification' && (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">⏳</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginBottom: 8 }}>Your application is being reviewed</h2>
              <p style={{ color: 'var(--text-sub)', fontSize: 14, lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
                We're verifying your residency and will email you within 24 hours with next steps to set up your internet service.
              </p>
              <LegalFooter />
            </div>
          )}

          {tenant?.status !== 'pending_verification' && tenant?.enrollment_type === 'opt_in_required' && (
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <strong style={{ color: '#0369a1' }}>Manual enrollment required</strong>
              <p style={{ fontSize: 13, color: '#0369a1', marginTop: 4 }}>
                Your lease type requires manual enrollment for internet service. Contact your property manager to get started.
              </p>
            </div>
          )}

          {tenant?.status !== 'pending_verification' && <>
          {/* Savings banner */}
          {sub && (
            <div className="savings-banner">
              <div>
                <div className="savings-text">You're saving vs. retail pricing every month</div>
                <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 2 }}>
                  {sub.plan_name}: ${fmt(sub.bulknet_price)}/mo vs. retail ${fmt(sub.retail_price)}/mo
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="savings-amount">${fmt(sub.savings_per_month)}<span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-sub)' }}>/mo</span></div>
                <div className="savings-label">{sub.savings_pct}% off retail · ${fmt(sub.savings_per_year)}/yr</div>
              </div>
            </div>
          )}

          {!sub && (
            <div style={{ background: '#fff5e0', border: '1px solid #f5c060', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
              <strong style={{ color: '#b85c00' }}>No active plan</strong>
              <p style={{ color: 'var(--text-sub)', fontSize: 13, marginTop: 4 }}>
                You don't have an active subscription yet.
              </p>
            </div>
          )}

          {/* Metric cards */}
          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-label">Current plan</div>
              <div className="metric-value" style={{ fontSize: 16 }}>{sub?.plan_name || '—'}</div>
              <div className="metric-sub">{sub ? `${sub.speed_mbps || ''}Mbps` : 'No active plan'}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Next payment</div>
              <div className="metric-value accent">{sub ? `$${fmt(sub.bulknet_price)}` : '—'}</div>
              <div className="metric-sub">{sub ? `Due ${fmtDate(sub.current_period_end)}` : '—'}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Plan status</div>
              <div className="metric-value green" style={{ fontSize: 16 }}>
                {sub?.status === 'active' ? 'Active' : sub?.status || '—'}
              </div>
              <div className="metric-sub">{sub?.cancel_at_period_end ? 'Cancels at period end' : 'Auto-renews monthly'}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Member since</div>
              <div className="metric-value" style={{ fontSize: 16 }}>
                {tenant?.enrolled_at ? new Date(tenant.enrolled_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
              </div>
            </div>
          </div>

          {/* Two column bottom */}
          <div className="two-col">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Recent invoices</div>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/tenant/billing')}>View all</button>
              </div>
              <div>
                {recent_invoices?.length > 0 ? recent_invoices.map((inv, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: i < recent_invoices.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(inv.created_at)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{sub?.plan_name || 'Service'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>${fmt(inv.amount)}</div>
                      <span className={`badge badge-${inv.status === 'paid' ? 'green' : inv.status === 'overdue' ? 'red' : 'orange'}`}>
                        {inv.status}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: '20px', color: 'var(--text-sub)', textAlign: 'center', fontSize: 13 }}>
                    No invoices yet
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><div className="card-title">Quick actions</div></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button className="btn btn-primary" style={{ width: '100%', textAlign: 'left' }} onClick={() => navigate('/tenant/billing')}>
                  💳 View billing & payments
                </button>
                <button className="btn btn-outline" style={{ width: '100%', textAlign: 'left' }} onClick={() => navigate('/tenant/plan')}>
                  📡 View or upgrade my plan
                </button>
                <button className="btn btn-outline" style={{ width: '100%', textAlign: 'left' }} onClick={() => navigate('/tenant/support')}>
                  🎫 Submit a support ticket
                </button>
              </div>
            </div>
          </div>
          </>}
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
